"use client";

import React from "react";
import { useFormContext } from "react-hook-form";
import { format, isWithinInterval, parseISO, startOfDay } from "date-fns";
import { CalendarIcon, Check, ChevronsUpDown, Search } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";

import { cn } from "@/lib/utils";
import {
  SERVICE_NAMES,
  AGE_OPTIONS,
  GENDER_OPTIONS,
  getTimeSlotsForDate,
} from "@/lib/constants";
import {
  getCountries,
  getCountryCallingCode,
  Country,
} from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";

// Required field label component with red asterisk
export const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="flex items-center gap-1">
    {children}
    <span className="text-red-500" aria-hidden="true">
      *
    </span>
  </span>
);

/**
 * PatientDetailsFields Component
 * Renders fields for the patient's core details: Name, Age, Gender.
 * A single Name field — not split into first/last — because the Patient
 * record itself only stores one name.
 */
export const PatientDetailsFields = () => {
  const { control } = useFormContext();

  return (
    <>
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <RequiredLabel>Patient Name</RequiredLabel>
            </FormLabel>
            <FormControl>
              <Input placeholder="Full name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          control={control}
          name="age"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <RequiredLabel>Age</RequiredLabel>
              </FormLabel>
              <Select
                key={`age-${field.value}`}
                onValueChange={field.onChange}
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger
                    className="w-full transition-colors"
                    aria-label="Select age"
                  >
                    <SelectValue placeholder="Select age">
                      {field.value || "Select age"}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {AGE_OPTIONS.map((age) => (
                    <SelectItem key={age} value={age}>
                      {age}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="gender"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <RequiredLabel>Gender</RequiredLabel>
              </FormLabel>
              <Select
                key={`gender-${field.value}`}
                onValueChange={field.onChange}
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger
                    className="w-full transition-colors"
                    aria-label="Select gender"
                  >
                    <SelectValue placeholder="Select gender">
                      {field.value || "Select gender"}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {GENDER_OPTIONS.map((gender) => (
                    <SelectItem key={gender} value={gender}>
                      {gender}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

/**
 * ServiceAndContactFields Component
 * Renders fields for contact information and service selection.
 * Service stays optional; Phone is required.
 */
export const ServiceAndContactFields = () => {
  const { control, setValue, watch } = useFormContext();
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const countries = getCountries();
  const countryLabels: Record<string, string> = en;

  const filteredCountries = countries.filter(
    (country) =>
      (countryLabels[country] || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      getCountryCallingCode(country).includes(search) ||
      country.toLowerCase().includes(search.toLowerCase())
  );

  const fullPhone = watch("phone") || "";
  const [selectedCountry, setSelectedCountry] = React.useState("IN");

  React.useEffect(() => {
    if (fullPhone.startsWith("+")) {
      for (const country of countries) {
        const code = getCountryCallingCode(country as Country);
        if (fullPhone.startsWith(`+${code}`)) {
          setSelectedCountry(country);
          if (code.length >= 3) break;
        }
      }
    }
  }, [fullPhone, countries]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const local = e.target.value.replace(/\D/g, "");
    const code = getCountryCallingCode(selectedCountry as Country);
    setValue("phone", `+${code}${local}`);
    setValue("countryCode", `+${code}`);
  };

  const getLocalNumber = (phone: string) => {
    const code = getCountryCallingCode(selectedCountry as Country);
    if (phone.startsWith(`+${code}`)) {
      return phone.slice(code.length + 1);
    }
    return phone.replace(/^\+\d+/, "");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-end gap-4 md:flex-row">
        <div className="w-full space-y-2">
          <FormLabel>
            <RequiredLabel>Mobile Number</RequiredLabel>
          </FormLabel>
          <div className="flex gap-2">
            <div className="w-[120px] shrink-0 md:w-[140px]">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                      "h-10 w-full justify-between px-3 transition-all",
                      "hover:border-primary/50 group hover:bg-primary/90 hover:text-white",
                      open && "border-primary ring-primary ring-1"
                    )}
                  >
                    <span className="flex items-center gap-2 overflow-hidden">
                      <span className="shrink-0 font-medium">
                        {selectedCountry}
                      </span>
                      <span className="truncate font-medium">
                        +{getCountryCallingCode(selectedCountry as Country)}
                      </span>
                    </span>
                    <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="border-primary/20 bg-popover w-[300px] p-0 shadow-2xl"
                  align="start"
                  sideOffset={4}
                >
                  <div className="bg-muted/30 sticky top-0 z-10 border-b p-3">
                    <div className="group relative">
                      <Search className="text-muted-foreground group-focus-within:text-primary absolute top-2.5 left-3 h-4 w-4 transition-colors" />
                      <input
                        className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:border-primary flex h-9 w-full rounded-md border py-1 pr-3 pl-9 text-sm shadow-sm transition-all focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Search country name or code..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div
                    className="custom-scrollbar max-h-[250px] overflow-x-hidden overflow-y-auto scroll-smooth p-1"
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {filteredCountries.map((country) => (
                      <button
                        key={country}
                        type="button"
                        className={cn(
                          "hover:bg-primary group relative flex w-full cursor-pointer items-center rounded-sm px-3 py-2.5 text-sm transition-all outline-none select-none",
                          selectedCountry === country
                            ? "bg-primary/10 text-primary font-semibold"
                            : "text-foreground hover:text-primary-foreground"
                        )}
                        onClick={() => {
                          const code = getCountryCallingCode(country);
                          setSelectedCountry(country);
                          setOpen(false);
                          setSearch("");
                          const local = getLocalNumber(fullPhone);
                          setValue("phone", `+${code}${local}`);
                          setValue("countryCode", `+${code}`);
                        }}
                      >
                        <span className="mr-2 flex-1 truncate text-left">
                          {countryLabels[country]}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 font-mono text-xs",
                            selectedCountry === country
                              ? "text-primary/70"
                              : "text-muted-foreground group-hover:text-white"
                          )}
                        >
                          +{getCountryCallingCode(country)}
                        </span>
                        {selectedCountry === country && (
                          <Check className="ml-2 h-4 w-4 shrink-0" />
                        )}
                      </button>
                    ))}
                    {filteredCountries.length === 0 && (
                      <div className="text-muted-foreground bg-muted/10 py-8 text-center text-sm">
                        No results found for &quot;{search}&quot;
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex-1">
              <FormField
                control={control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Input
                        placeholder="9876543210"
                        value={getLocalNumber(field.value)}
                        onChange={handlePhoneChange}
                        className="hover:border-primary/50 focus-visible:ring-primary focus-visible:border-primary h-10 transition-all"
                      />
                    </FormControl>
                    <FormMessage className="mt-1" />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full">
        <FormField
          control={control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Service (Optional)</FormLabel>
              <Select
                key={`service-${field.value}`}
                onValueChange={field.onChange}
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger
                    className="hover:border-primary/50 focus:ring-primary h-10 w-full transition-all"
                    aria-label="Select service"
                  >
                    <SelectValue placeholder="Select service">
                      {field.value || "Select service"}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px]">
                  {SERVICE_NAMES.map((service) => (
                    <SelectItem
                      key={service}
                      value={service}
                      className="focus:bg-primary focus:text-primary-foreground cursor-pointer transition-colors"
                    >
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

interface AppointmentFieldsProps {
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  slotCounts: Record<string, number>;
  isLoadingSlots: boolean;
  blockedRanges?: { from: string; to: string; reason?: string }[];
}

/**
 * AppointmentFields Component
 * Renders date and time selection fields.
 * Time slots are never disabled for being "taken" — multiple patients can
 * share the same exact time. A small count badge shows how many people are
 * already booked at that time, purely informational.
 */
export const AppointmentFields = ({
  selectedDate,
  setSelectedDate,
  slotCounts,
  isLoadingSlots,
  blockedRanges = [],
}: AppointmentFieldsProps) => {
  const { control } = useFormContext();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);

  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField
        control={control}
        name="preferredDate"
        render={() => (
          <FormItem className="flex flex-col">
            <FormLabel>
              <RequiredLabel>Date</RequiredLabel>
            </FormLabel>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    className={cn(
                      "hover:border-primary/50 h-10 w-full pl-3 text-left font-normal transition-all",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    {selectedDate ? (
                      format(selectedDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon
                      className="ml-auto h-4 w-4 opacity-50"
                      aria-hidden="true"
                    />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="border-primary/20 w-auto p-0 shadow-2xl"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (!date) {
                      setSelectedDate(undefined);
                      return;
                    }
                    const isBlocked = blockedRanges.some((range) => {
                      const start = startOfDay(parseISO(range.from));
                      const end = startOfDay(parseISO(range.to));
                      return isWithinInterval(date, {
                        start: start <= end ? start : end,
                        end: end >= start ? end : start,
                      });
                    });
                    if (isBlocked) return;
                    setSelectedDate(date);
                    setIsCalendarOpen(false);
                  }}
                  disabled={(date) => {
                    if (date < today) return true;
                    if (date > maxDate) return true;
                    const isBlocked = blockedRanges.some((range) => {
                      const start = startOfDay(parseISO(range.from));
                      const end = startOfDay(parseISO(range.to));
                      return isWithinInterval(date, {
                        start: start <= end ? start : end,
                        end: end >= start ? end : start,
                      });
                    });
                    return isBlocked;
                  }}
                  modifiers={{
                    blocked: (date) =>
                      blockedRanges.some((range) => {
                        const start = startOfDay(parseISO(range.from));
                        const end = startOfDay(parseISO(range.to));
                        return isWithinInterval(date, {
                          start: start <= end ? start : end,
                          end: end >= start ? end : start,
                        });
                      }),
                  }}
                  modifiersClassNames={{
                    blocked: "pointer-events-auto opacity-50 hover:bg-transparent",
                  }}
                  components={{
                    // @ts-expect-error - DayContent is valid in react-day-picker but missing in shadcn types
                    DayContent: (props: { date: Date; activeModifiers: Record<string, boolean> }) => {
                      const { date, activeModifiers } = props;
                      let reason = null;
                      if (activeModifiers.blocked) {
                        const range = blockedRanges.find((r) => {
                          const start = startOfDay(parseISO(r.from));
                          const end = startOfDay(parseISO(r.to));
                          return isWithinInterval(date, {
                            start: start <= end ? start : end,
                            end: end >= start ? end : start,
                          });
                        });
                        reason = range?.reason;
                      }
                      if (reason) {
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex h-full w-full items-center justify-center cursor-not-allowed">
                                {date.getDate()}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{reason}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      return (
                        <span className="flex h-full w-full items-center justify-center">
                          {date.getDate()}
                        </span>
                      );
                    },
                  }}
                  className="pointer-events-auto p-3"
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="preferredTime"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <RequiredLabel>Time</RequiredLabel>
            </FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              disabled={!selectedDate || isLoadingSlots}
            >
              <FormControl>
                <SelectTrigger
                  className="hover:border-primary/50 focus:ring-primary h-10 w-full transition-all"
                  aria-label="Select time slot"
                >
                  <SelectValue
                    placeholder={
                      !selectedDate
                        ? "Select date first"
                        : isLoadingSlots
                          ? "Loading..."
                          : "Select time"
                    }
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="max-h-[300px]">
                {getTimeSlotsForDate(selectedDate).map((time) => {
                  const count = slotCounts[time] || 0;
                  return (
                    <SelectItem
                      key={time}
                      value={time}
                      className="focus:bg-primary focus:text-primary-foreground cursor-pointer transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        {time}
                        {count > 0 && (
                          <span className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">
                            ({count} booked)
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
